import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, 
  Card, 
  List,
  Input, 
  Space, 
  Typography, 
  Avatar, 
  Tag, 
  Modal,
  Descriptions,
  Badge,
  message,
  Spin,
  Row,
  Col,
  Pagination,
  Button
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined,
  CalendarOutlined,
  CloudServerOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import apiService from '../services/ApiService';
import SubscriptionCard from '../components/SubscriptionCard';
import { hapticFeedbackImpactOccurred } from '@telegram-apps/sdk-react';

const { Content, Header } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  
  // Состояние для списка пользователей
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20, // Уменьшим для мобильных устройств
    total: 0
  });

  // Состояние для модального окна профиля
  const [profileModal, setProfileModal] = useState({
    visible: false,
    loading: false,
    user: null,
    subscriptions: []
  });

  // Состояние для модального окна подтверждения удаления
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    loading: false,
    subscriptionId: null
  });

  useEffect(() => {
    // Проверяем права администратора
    if (!user || !user.is_admin) {
      navigate('/admin');
      return;
    }

    loadUsers();
  }, [user, navigate, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    // Настройка BackButton для возврата в админ-панель
    if (window.Telegram?.WebApp?.BackButton) {
      const backButton = window.Telegram.WebApp.BackButton;
      
      const handleBackClick = () => {
        navigate('/admin');
      };

      // Показываем кнопку назад
      backButton.show();
      backButton.onClick(handleBackClick);

      // Cleanup при размонтировании
      return () => {
        backButton.offClick(handleBackClick);
        backButton.hide();
      };
    }
  }, [navigate]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdminUsersList(
        user.user_id,
        pagination.current,
        pagination.pageSize,
        searchText
      );

      setUsers(response.users || []);
      setPagination(prev => ({
        ...prev,
        total: response.total_count || 0
      }));
    } catch (error) {
      message.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id, pagination.current, pagination.pageSize, searchText]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize
    }));
  };

  const handleViewProfile = async (userId) => {
    try {
      setProfileModal(prev => ({ ...prev, visible: true, loading: true }));
      
      const response = await apiService.getAdminUserProfile(userId, user.user_id);
      
      setProfileModal({
        visible: true,
        loading: false,
        user: response.user,
        subscriptions: response.subscriptions || []
      });
    } catch (error) {
      message.error('Ошибка загрузки профиля пользователя');
      setProfileModal(prev => ({ ...prev, visible: false, loading: false }));
    }
  };

  const handleAdminDeleteSubscription = async (subscriptionId) => {
    // Показываем модал подтверждения
    setDeleteModal({ visible: true, loading: false, subscriptionId: subscriptionId });
  };

  const confirmDeleteSubscription = async () => {
    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));
      hapticFeedbackImpactOccurred('heavy');
      
      await apiService.adminDeleteSubscription(
        deleteModal.subscriptionId,
        profileModal.user.id,
        user.user_id
      );
      
      message.success('Подписка успешно удалена');
      
      // Обновляем список подписок в модальном окне
      setProfileModal(prev => ({
        ...prev,
        subscriptions: prev.subscriptions.filter(sub => sub.id !== deleteModal.subscriptionId)
      }));
      
      // Закрываем модал
      setDeleteModal({ visible: false, loading: false, subscriptionId: null });
      
    } catch (error) {
      message.error('Ошибка удаления подписки');
    } finally {
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'expired':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'expiring_soon':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Активна';
      case 'expired':
        return 'Истекла';
      case 'expiring_soon':
        return 'Истекает';
      case 'inactive':
        return 'Неактивна';
      default:
        return 'Неизвестно';
    }
  };

  const formatTraffic = (bytes) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(1)} ГБ`;
  };

  const renderUserCard = (userItem) => (
    <Card 
      key={userItem.id}
      hoverable
      style={{ marginBottom: '12px' }}
      bodyStyle={{ padding: '16px' }}
      onClick={() => handleViewProfile(userItem.id)}
    >
      <Row gutter={[16, 8]} align="middle" wrap={false}>
        <Col>
          <Avatar 
            src={userItem.photo_url} 
            icon={<UserOutlined />}
            size={64}
          />
        </Col>
        <Col flex="auto">
          <Space direction="vertical" size="small" style={{ minWidth: 0, marginLeft: '16px' }}>
            <Text style={{ fontSize: '16px' }}>
              <strong>{userItem.first_name || 'Без имени'}</strong>
            </Text>
            <Text style={{ fontSize: '14px' }} type="secondary">
              @{userItem.username || 'без username'}
            </Text>
            <Text style={{ fontSize: '12px' }} type="secondary">
              ID: {userItem.user_id}
            </Text>
          </Space>
        </Col>
        <Col>
          <Space direction="vertical" size="small" style={{ alignItems: 'flex-end' }}>
            <Tag style={{ margin: 0 }}>
              {userItem.is_admin ? 'Админ' : 'Пользователь'}
            </Tag>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: 'transparent', 
        padding: '0 16px',
        borderBottom: '1px solid var(--ant-color-border)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%'
        }}>
          <Title level={4} style={{ margin: 0 }}>
            Пользователи
          </Title>
        </div>
      </Header>

      <Content style={{ padding: '16px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Поиск */}
          <Search
            placeholder="Поиск по ID, username или имени"
            allowClear
            enterButton="Поиск"
            size="large"
            onSearch={handleSearch}
            loading={loading}
          />

          {/* Список пользователей */}
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : users.length > 0 ? (
              <>
                {users.map(renderUserCard)}
                
                {/* Пагинация */}
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} из ${total}`
                    }
                    onChange={handlePageChange}
                    size="small"
                  />
                </div>
              </>
            ) : (
              <Card>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary">
                    {searchText ? 'Пользователи не найдены' : 'Нет пользователей'}
                  </Text>
                </div>
              </Card>
            )}
          </div>
        </Space>
      </Content>

      {/* Модальное окно профиля пользователя */}
      <Modal
        title="Профиль пользователя"
        open={profileModal.visible}
        onCancel={() => setProfileModal(prev => ({ ...prev, visible: false }))}
        footer={null}
        width="95%"
        style={{ maxWidth: '600px' }}
        destroyOnClose
      >
        {profileModal.loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : profileModal.user ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Информация о пользователе */}
            <Card size="small" title="Основная информация">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <Avatar 
                  src={profileModal.user.photo_url} 
                  icon={<UserOutlined />}
                  size={64}
                  style={{ marginRight: '16px' }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {profileModal.user.first_name || 'Без имени'}
                  </Title>
                  <Text type="secondary">
                    @{profileModal.user.username || 'без username'}
                  </Text>
                </div>
              </div>
              
              <Descriptions column={1} size="small">              
                <Descriptions.Item label="Дата регистрации">
                  {profileModal.user.created_at ? 
                    new Date(profileModal.user.created_at).toLocaleString('ru-RU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 
                    'Неизвестно'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Telegram ID">
                  <Text copyable={{ text: profileModal.user.user_id.toString() }}>
                    {profileModal.user.user_id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Тема">
                  <Tag color={profileModal.user.theme_mode === 'dark' ? 'default' : 'gold'}>
                    {profileModal.user.theme_mode === 'dark' ? 'Тёмная' : 'Светлая'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Подписки пользователя */}
            <Card 
              size="small" 
              title={
                <Space>
                  <CloudServerOutlined />
                  Подписки ({profileModal.subscriptions.length})
                </Space>
              }
            >
              {profileModal.subscriptions.length > 0 ? (
                <List
                  dataSource={profileModal.subscriptions}
                  renderItem={(subscription) => (
                    <div style={{ marginBottom: 12 }}>
                      <SubscriptionCard subscription={subscription} style={{ width: '100%' }} onDelete={handleAdminDeleteSubscription} />
                    </div>
                    /* <List.Item style={{ padding: '8px 0' }}>
                      { <Card size="small" style={{ width: '100%' }} bodyStyle={{ padding: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                              {subscription.server_name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)', marginBottom: '4px' }}>
                              <CalendarOutlined style={{ marginRight: '4px' }} />
                              {new Date(subscription.expiry_date).toLocaleDateString('ru-RU')}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>
                              {formatTraffic(subscription.used_traffic)} / {formatTraffic(subscription.traffic)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <Badge 
                              status={subscription.status === 'active' ? 'success' : 
                                     subscription.status === 'expiring_soon' ? 'warning' : 'error'}
                              text={getStatusText(subscription.status)}
                            />
                          </div>
                        </div>
                      </Card> }
                    </List.Item>*/
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">Подписок не найдено</Text>
                </div>
              )}
            </Card>
          </Space>
        ) : null}
      </Modal>

      {/* Модальное окно подтверждения удаления */}
      <Modal
        title="Подтверждение удаления"
        open={deleteModal.visible}
        onCancel={() => setDeleteModal(prev => ({ ...prev, visible: false }))}
        footer={[
          <Button
            key="cancel"
            onClick={() => setDeleteModal(prev => ({ ...prev, visible: false }))}
            disabled={deleteModal.loading}
          >
            Отмена
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={confirmDeleteSubscription}
            loading={deleteModal.loading}
          >
            Да, удалить
          </Button>,
        ]}
        destroyOnClose
      >
        <p>Вы уверены, что хотите удалить эту подписку? Пользователь получит уведомление.</p>
      </Modal>
    </Layout>
  );
};

export default AdminUsersPage; 